import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  EntrySignalGroup,
  RZ_TRIGGER_IDS,
  S09CreateDraft,
  TriggerConditionOption,
  WorkflowDefinition,
  blankCreateDraft,
  entrySignalTooltip,
  triggerHint,
  triggerMeta,
} from "./s09-workflow.models";
import { WorkflowService } from "../../service/workflow.service";

@Component({
  selector: "app-s09-create-dialog",
  templateUrl: "./s09-create-dialog.component.html",
  styleUrls: ["./s09-workflow.scss"],
  standalone: false,
})
export class S09CreateDialogComponent implements OnInit {
  @Input() brandName = "Brand";
  @Input() brandKey: number | null = null;
  @Input() definitions: WorkflowDefinition[] = [];
  @Input() sourceId: number | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() create = new EventEmitter<S09CreateDraft>();

  draft: S09CreateDraft = blankCreateDraft();
  startFrom: "blank" | "clone" = "blank";
  err = "";
  triggerIds = RZ_TRIGGER_IDS as unknown as string[];

  signalGroups: EntrySignalGroup[] = [];
  signalsLoading = false;
  signalsError = "";

  allConditions: TriggerConditionOption[] = [];
  conditionsLoading = false;
  conditionsHint = "";

  constructor(private workflowService: WorkflowService) {}

  ngOnInit(): void {
    if (this.sourceId) this.useClone(this.sourceId);
    this.loadEntrySignals();
    this.loadConditions();
  }

  useBlank(): void {
    this.startFrom = "blank";
    this.draft.sourceWfDefId = null;
  }

  useClone(id?: number | null): void {
    this.startFrom = "clone";
    const previous = this.definitions.find((d) => d.wf_def_id === this.draft.sourceWfDefId);
    const previousName = previous ? "Copy of " + (previous.wf_name || "workflow") : "";
    const picked = id || this.draft.sourceWfDefId || this.definitions[0]?.wf_def_id || null;
    this.draft.sourceWfDefId = picked;
    const def = this.definitions.find((d) => d.wf_def_id === picked);
    const suggested = def ? "Copy of " + (def.wf_name || "workflow") : "";
    if (!this.draft.name.trim() || this.draft.name === previousName) this.draft.name = suggested;
  }

  get trigHint(): string {
    return triggerHint(this.draft.trigger);
  }

  get conditionsForTrigger(): TriggerConditionOption[] {
    const t = (this.draft.trigger || "").toLowerCase();
    return this.allConditions.filter((c) => c.trigger_type?.toLowerCase() === t);
  }

  trigPath(id: string): string {
    return triggerMeta(id).path;
  }

  trigLabel(id: string): string {
    return triggerMeta(id).label;
  }

  pickGroup(g: string): void {
    this.draft.group = g;
  }

  pickTrigger(id: string): void {
    this.draft.trigger = id;
    // Clear threshold when switching trigger so stale condition isn't kept
    if (
      this.draft.threshold &&
      !this.conditionsForTrigger.some((c) => c.condition_label === this.draft.threshold)
    ) {
      this.draft.threshold = "";
    }
  }

  pickPrio(p: string): void {
    this.draft.priority = p;
  }

  pickCondition(label: string): void {
    this.draft.threshold = this.draft.threshold === label ? "" : label;
  }

  toggleCode(code: string): void {
    const i = this.draft.codes.indexOf(code);
    if (i >= 0) this.draft.codes.splice(i, 1);
    else this.draft.codes.push(code);
  }

  isCodeOn(code: string): boolean {
    return this.draft.codes.includes(code);
  }

  signalTip(s: { signal_code?: string; signal_label?: string }): string {
    return entrySignalTooltip(s);
  }

  addOther(): void {
    const parts = String(this.draft.sigOther || "")
      .split(/[,;]/)
      .map((x) => x.trim())
      .filter(Boolean);
    parts.forEach((p) => {
      if (!this.draft.codes.includes(p)) this.draft.codes.push(p);
    });
    this.draft.sigOther = "";
  }

  removeCode(code: string): void {
    this.draft.codes = this.draft.codes.filter((c) => c !== code);
  }

  submit(): void {
    this.err = "";
    if (!this.draft.code.trim()) {
      this.err = "Code is required.";
      return;
    }
    if (!this.draft.name.trim()) {
      this.err = "Workflow name is required.";
      return;
    }
    if (!this.draft.trigger) {
      this.err = "Trigger type is required.";
      return;
    }
    if (this.startFrom === "clone" && !this.draft.sourceWfDefId) {
      this.err = "Choose a workflow to clone.";
      return;
    }
    this.create.emit({
      ...this.draft,
      codes: [...this.draft.codes],
      caseType: "",
      caseTypeKey: null,
      caseTypeCode: "",
      feed: "",
      hub: "",
      from: "",
      sourceWfDefId: this.startFrom === "clone" ? this.draft.sourceWfDefId : null,
    });
  }

  private loadEntrySignals(): void {
    const brandKey = this.resolveBrandKey();
    if (!brandKey) {
      this.signalsError = "Brand is not set — cannot load signals.";
      return;
    }
    this.signalsLoading = true;
    this.workflowService.getEntrySignals(brandKey).subscribe((groups) => {
      this.signalsLoading = false;
      this.signalGroups = groups || [];
      if (!this.signalGroups.length) {
        this.signalsError =
          "No signals configured. Open the Signals tab to add groups and reason codes.";
      }
    });
  }

  private loadConditions(): void {
    const brandKey = this.resolveBrandKey();
    if (!brandKey) {
      this.conditionsHint = "Brand is not set — cannot load conditions.";
      return;
    }
    this.conditionsLoading = true;
    this.workflowService.getTriggerConditions(brandKey).subscribe((rows) => {
      this.conditionsLoading = false;
      this.allConditions = rows || [];
      this.conditionsHint = this.allConditions.length
        ? ""
        : "No conditions configured. Run scripts/workflow-api/05_create_trigger_condition_config.sql and 06_seed_trigger_conditions.sql.";
    });
  }

  private resolveBrandKey(): number {
    if (this.brandKey) return Number(this.brandKey);
    try {
      const raw = sessionStorage.getItem("brandKeyForFilters");
      if (raw) {
        const parsed = JSON.parse(raw);
        const k = Number(parsed?.brand_key ?? parsed);
        if (k) return k;
      }
    } catch {
      /* ignore */
    }
    return 200104;
  }
}
